import { IntegrationError } from '@superfunnel/types';
import Badge from '@superfunnel/ui-core/v2/Badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@superfunnel/ui-core/v2/Tooltip';
import { Timestamp } from 'firebase/firestore';
import { Check, Copy, WarningCircle, WarningTriangle } from 'iconoir-react';
import { ReactNode, useState } from 'react';

// =============================================================================
// Types
// =============================================================================

interface IntegrationHealthBadgeProps {
  lastErrors?: IntegrationError[];
  consecutiveFailures?: number;
}

type HealthStatus = 'healthy' | 'warning' | 'failing';

interface HealthConfig {
  label: string;
  variant: 'success' | 'warning' | 'error';
  icon: ReactNode;
}

// =============================================================================
// Constants
// =============================================================================

const HEALTH_CONFIG: Record<HealthStatus, HealthConfig> = {
  healthy: {
    label: 'Healthy',
    variant: 'success',
    icon: <Check className="size-3" />,
  },
  warning: {
    label: 'Warning',
    variant: 'warning',
    icon: <WarningTriangle className="size-3" />,
  },
  failing: {
    label: 'Failing',
    variant: 'error',
    icon: <WarningCircle className="size-3" />,
  },
};

/** Errors older than 24 hours are hidden */
const ERROR_AGE_THRESHOLD_MS = 24 * 60 * 60 * 1000;

// =============================================================================
// Helpers
// =============================================================================

function getHealthStatus(consecutiveFailures?: number): HealthStatus {
  if (!consecutiveFailures || consecutiveFailures === 0) {
    return 'healthy';
  }
  if (consecutiveFailures >= 3) {
    return 'failing';
  }
  return 'warning';
}

function getTimestampMs(timestamp: Timestamp | { _seconds: number }): number {
  if (timestamp instanceof Timestamp) {
    return timestamp.toMillis();
  }
  // Handle serialized Firestore timestamp
  return timestamp._seconds * 1000;
}

function isErrorRecent(error: IntegrationError): boolean {
  const errorTime = getTimestampMs(
    error.timestamp as Timestamp | { _seconds: number },
  );
  return Date.now() - errorTime < ERROR_AGE_THRESHOLD_MS;
}

function formatErrorTime(error: IntegrationError): string {
  const errorTime = getTimestampMs(
    error.timestamp as Timestamp | { _seconds: number },
  );
  return new Date(errorTime).toLocaleString();
}

// =============================================================================
// Error Row Component
// =============================================================================

function ErrorRow({ error }: { error: IntegrationError }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(error.message);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="border-t border-gray-100 pt-2 first:border-t-0 first:pt-0">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs text-gray-500">{formatErrorTime(error)}</div>
        <button
          type="button"
          onClick={handleCopy}
          className="shrink-0 rounded p-0.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          title="Copy error message"
        >
          {copied ? (
            <Check className="size-3 text-green-500" />
          ) : (
            <Copy className="size-3" />
          )}
        </button>
      </div>
      <div
        className="mt-0.5 line-clamp-3 cursor-help text-xs text-gray-700"
        title={error.message}
      >
        {error.message}
      </div>
    </div>
  );
}

// =============================================================================
// Component
// =============================================================================

export default function IntegrationHealthBadge({
  lastErrors,
  consecutiveFailures,
}: IntegrationHealthBadgeProps) {
  const status = getHealthStatus(consecutiveFailures);
  const config = HEALTH_CONFIG[status];

  // When there are active failures, show all errors regardless of age
  // but cap to consecutiveFailures count so we don't show stale ones.
  // Only apply the 24h recency filter for healthy integrations.
  const recentErrors =
    status !== 'healthy'
      ? (lastErrors ?? []).slice(0, consecutiveFailures)
      : (lastErrors ?? []).filter(isErrorRecent);

  // If healthy and no recent errors, just show simple badge
  if (status === 'healthy' && recentErrors.length === 0) {
    return (
      <Badge variant={config.variant}>
        {config.icon}
        {config.label}
      </Badge>
    );
  }

  // Show badge with tooltip for errors
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span>
          <Badge variant={config.variant} className="cursor-help">
            {config.icon}
            {config.label}
          </Badge>
        </span>
      </TooltipTrigger>
      <TooltipContent
        variant="white"
        className="max-w-sm p-3"
        side="bottom"
        align="start"
      >
        <div className="space-y-2">
          <div className="text-sm font-medium text-gray-900">
            {status === 'failing'
              ? `${consecutiveFailures}+ consecutive failures`
              : status === 'warning'
                ? `${consecutiveFailures} recent failure${consecutiveFailures === 1 ? '' : 's'}`
                : 'Recent errors'}
          </div>
          {recentErrors.length > 0 && (
            <div className="space-y-2">
              {recentErrors.map((error) => (
                <ErrorRow
                  key={`${getTimestampMs(error.timestamp as Timestamp | { _seconds: number })}-${error.message.slice(0, 20)}`}
                  error={error}
                />
              ))}
            </div>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
